import { _ } from "libs";
import React, { useEffect, useState, FC } from "react";
import { useHistory } from "react-router-dom";
import {
	sendBuddyRequest,
	removeBuddy,
	loadFriends,
	openOfficialRatingPopup,
	closePlayerPopup,
	showPlayerChampionshipProfile,
	openPlayerPopup,
} from "../actions";
import { Championship, ChampionshipLeague, Profile } from "../types";
import Loader from "./loader";
import ClubsList from "./clubsList";
import Avatar from "./avatar";
import { formatName } from "../libs/formatName";
import withRedux from "../withRedux";
import ChatLink from "./chatLink";
import DropdownButtonMenu from "./dropdownButtonMenu";
import { getLeagueInfo } from "./items/player/playerHelper";

type Props = {
	profile: Profile;
	onClose;
	showHeader: boolean;
	isMyProfile: boolean;
	loadFriends;
	showPlayerChampionshipProfile;
	showChallengeButton?: boolean;
	challengeButtonParams?: object;
	contextParams?: any;
	className?: string;
};

type RatingProps = {
	userId: string;
	country: null | string;
	ratingValue: null | string;
	ITN: null | string;
	ITNName: null | string;
	officialRating?: {
		code?: string;
		name?: string;
	};
};

const PlayerProfile: FC<Props> = ({
	profile,
	onClose,
	showHeader = true,
	isMyProfile = false,
	loadFriends,
	showPlayerChampionshipProfile,
	challengeButtonParams = {},
	contextParams = {},
	showChallengeButton,
	className,
}: Props) => {
	const [playerProfile, setProfile] = useState(profile as Profile);

	const history = useHistory();

	useEffect(() => {
		setProfile(profile);
	}, [profile.userId]);

	const addBuddy = () => {
		sendBuddyRequest(playerProfile.userId).then(data => {
			setProfile({ ...profile, userRelationship: data.userRelationship });
		});
	};

	const deleteBuddy = () => {
		if (window.confirm(_("buddy.remove.prompt"))) {
			removeBuddy(playerProfile.userId).then(data => {
				setProfile({ ...profile, userRelationship: data.userRelationship });
				loadFriends();
			});
		}
	};

	if (!playerProfile) {
		return <Loader />;
	}

	const league: ChampionshipLeague | null = getLeagueInfo(contextParams?.championship, contextParams?.leagueId);

	const openPlayerChampionshipModal = (playerId: string, championship: Championship) => {
		closePlayerPopup();
		const infoFunction = () => {
			history.push(`/championship/${championship.uuid}`);
		};
		showPlayerChampionshipProfile(
			playerId,
			championship,
			() => {
				openPlayerPopup(playerId, showChallengeButton, challengeButtonParams);
			},
			infoFunction
		);
	};

	return (
		<div className={`playerProfile ${className}`}>
			{showHeader ? (
				<div className="playerProfileHeader">
					<h5>{/* {_("playerplayerProfile.heading")} */}</h5>
					<div className="close" onClick={onClose} />
				</div>
			) : null}
			<div className="playerProfileBody">
				<div className="playerProfileImageWrapper">
					<Avatar
						photoUrl={playerProfile.photoUrl}
						className="main"
						size="thumb6"
						zoom={true}
						league={league}
						showLeagueBadge={true}
					/>
				</div>
				<div className="name">{formatName(playerProfile.firstName, playerProfile.lastName)}</div>

				{playerProfile.coachType ? (
					<div className={`coachType ${playerProfile.coachType}`}>
						{_("coachType." + playerProfile.coachType)}
					</div>
				) : null}

				{playerProfile.sport ? (
					<div className="sports">
						<div className="sport badge">{_("courtSport." + playerProfile.sport)}</div>
					</div>
				) : null}

				<PlayerProfileRating {...playerProfile} />

				{!isMyProfile ? (
					<div className="actions">
						{playerProfile.userRelationship === "none" ? (
							<span onClick={addBuddy} className="button addFriend">
								{_("player.addFriendBtn")}
							</span>
						) : null}
						{playerProfile.userRelationship === "requested" ? (
							<span className="button addFriend disabled">{_("player.friendRequestSentBtn")}</span>
						) : null}
						<ChatLink
							className="button sendMessage"
							ids={[playerProfile.userId]}
							label={_("player.sendMessageBtn")}
						/>
						{playerProfile.userRelationship === "accepted" ? (
							<>
								<DropdownButtonMenu>
									<span
										onClick={() => {
											deleteBuddy();
										}}
									>
										{_("player.removeFromBuddiesBtn")}
									</span>
								</DropdownButtonMenu>
							</>
						) : null}
					</div>
				) : null}

				<div className="info">
					<h6>{_("profile.aboutMe.heading")}</h6>
					{playerProfile.aboutMe ? <p className="aboutMe">{playerProfile.aboutMe}</p> : null}
					<div className="personalInfo">
						{playerProfile.gender ? <div className="gender" data-gender={playerProfile.gender} /> : null}
						{playerProfile.age ? <div className="age">{playerProfile.age}</div> : null}
						{playerProfile.age && playerProfile.city ? <span>&nbsp;&ndash;&nbsp;</span> : null}
						{playerProfile.city ? <div className="city">{playerProfile.city}</div> : null}
					</div>
				</div>

				{Array.isArray(playerProfile.championships) && playerProfile.championships.length > 0 && (
					<div className="competitions mb-5">
						<h6>{_("profile.championships.heading")}</h6>
						<div className="table defaultSpacing">
							{playerProfile.championships.map((championship, i) => {
								const championshipLeagueInfo = getLeagueInfo(championship, championship.league?.id);
								return (
									<div
										className="cell capsule imageBg defaultChampionshipImg overlay50"
										key={"player.profile.championship." + i}
										style={
											championship.settings.pictureUrl
												? { backgroundImage: `url('${championship.settings.pictureUrl}')` }
												: null
										}
									>
										<div
											className="item championship large"
											style={{ cursor: "pointer" }}
											onClick={() =>
												openPlayerChampionshipModal(playerProfile.userId, championship)
											}
										>
											<div className="itemHeader">
												<div className="title">{championship.name}</div>
												<div className="meta">
													{championship.winRate
														? _(
																"championship.playedMatchesWinRate",
																championship.matchesPlayed,
																{
																	matchesPlayed: championship.matchesPlayed,
																	winRate: championship.winRate,
																}
														  )
														: _("championship.playedMatches", championship.matchesPlayed, {
																matchesPlayed: championship.matchesPlayed,
														  })}
												</div>
											</div>
											{championship.league?.id || championship.matchesPlayed > 0 ? (
												<div className="itemBody">
													<div className="ranking">
														<div className="rankingLabel">{_("championship.ranking")}</div>
														<div className="position">{championship.rank?.rank}</div>
													</div>
													{championship.league && (
														<div className="leagueRanking">
															<div className="shield">
																<svg
																	width="38px"
																	height="48px"
																	viewBox="0 0 51 64"
																	version="1.1"
																	xmlns="http://www.w3.org/2000/svg"
																>
																	<defs>
																		<linearGradient
																			x1="50%"
																			y1="0%"
																			x2="50%"
																			y2="100%"
																			id={
																				"leagueShieldGradient_" +
																				championship.league.id
																			}
																		>
																			<stop
																				stopColor={
																					championshipLeagueInfo?.colors
																						?.gradientStart
																				}
																				offset="0%"
																			/>
																			<stop
																				stopColor={
																					championshipLeagueInfo?.colors
																						?.gradientEnd
																				}
																				offset="100%"
																			/>
																		</linearGradient>
																	</defs>
																	<g
																		id="Championships"
																		stroke="none"
																		strokeWidth="1"
																		fill="none"
																		fillRule="evenodd"
																	>
																		<path
																			d="M0,3.35809843 C17.0002489,-1.24115157 33.9996764,-0.995901864 51,3.35809843 L51,3.35809843 L50.9804272,5.80890031 L50.8323547,27.8350888 L50.8323547,27.9425952 C50.3745248,34.9388744 48.9822986,41.4337683 45.3213241,47.421359 C41.670498,53.3904578 35.8012026,58.7783894 26.4054164,63.5405791 L26.4054164,63.5405791 L25.4999571,64 L24.5944979,63.5405791 C15.198637,58.7783452 9.32934912,53.3904726 5.67859019,47.421359 C2.01761562,41.433761 0.625394004,34.9388965 0.167559545,27.9425952 L0.167559545,27.9425952 L0.167559545,27.8350888 L0.0195728309,5.80890031 L0,3.35809843 Z M47.8125,5.94835886 C32.9372165,2.24640086 18.0619331,2.32155255 3.1875,5.94835886 C3.2335671,11.9496755 3.28050332,20.1692934 3.32743954,26.1706248 C4.160946,39.5269074 8.35219908,50.8886542 25.4995656,60 C42.6469093,50.8886542 46.8381395,39.5269074 47.6716917,26.1706248 C47.7186276,20.1693082 47.7655637,11.9496903 47.8125,5.94835886 Z M37.449372,24.5054504 C39.9326406,24.3930722 42.2884362,24.4223822 44.5165079,24.5928619 C44.5123238,25.1436326 44.5083155,25.6778219 44.5043072,26.1930369 C43.789834,37.7017092 40.1977194,48.549051 25.5,56.4 C13.3754152,49.9235278 8.80777363,41.407989 7.17900357,32.1642169 L7.24477713,32.1294154 C9.46938077,30.9550078 11.8212712,29.9127142 14.3009243,29.0039507 C15.9978154,33.5578766 20.3717674,36.8 25.5,36.8 C32.1015286,36.8 37.453125,31.427417 37.453125,24.8 C37.453125,24.7016354 37.4519461,24.6035472 37.449372,24.5054504 Z M28.2988641,21.55016 C29.8431919,23.100896 31.3051228,23.808768 32.7667031,23.710032 C33.2297034,23.679232 33.6886716,23.563952 34.1476397,23.36472 C34.2240759,23.832 34.265625,24.311072 34.265625,24.8 C34.265625,29.660064 30.3410794,33.6 25.5,33.6 C25.0804772,33.6 24.6684928,33.568496 24.2647481,33.511472 C24.4519819,33.154016 24.5885503,32.774032 24.6460528,32.36536 C24.8455584,30.944864 24.0903122,29.431968 22.2691659,27.60368 C20.4692325,25.796512 20.3572078,22.96872 22.0196963,21.299712 C23.6820094,19.630704 26.4987553,19.743168 28.2988641,21.55016 Z M25.5,16 C29.4196369,16 32.7376012,18.583152 33.8589,22.146096 C33.4626937,22.345152 33.0750778,22.4548 32.6855334,22.480672 C31.5977194,22.553008 30.4462669,21.96376 29.1664856,20.67896 C26.8881244,18.391664 23.2928156,18.279024 21.1518994,20.428512 C19.0111584,22.577824 19.1230078,26.187408 21.4013691,28.474704 C22.9062516,29.985488 23.5699847,31.201824 23.4307866,32.193232 C23.3802966,32.55192 23.2300537,32.891776 23.00355,33.235856 C19.3789641,32.156272 16.734375,28.78904 16.734375,24.8 C16.734375,19.939936 20.6589206,16 25.5,16 Z M44.625,8.76817104 C44.5971509,12.347789 44.5693019,16.843324 44.5414529,20.9867242 C42.0993086,20.8093653 39.5247109,20.7918654 36.8186122,20.9321052 C35.2154319,16.2024579 30.7535071,12.8 25.5,12.8 C18.8984714,12.8 13.546875,18.172583 13.546875,24.8 C13.546875,25.0159526 13.5525571,25.2305728 13.5637644,25.4425462 C11.1576477,26.3038442 8.85892958,27.2833649 6.66788761,28.3813221 C6.59755763,27.6552938 6.540428,26.9255047 6.49495795,26.1930369 C6.45472691,21.0218857 6.41449588,13.9393095 6.375,8.76817104 C19.1245206,5.64307054 31.8747603,5.57831478 44.625,8.76817104 Z"
																			fill={
																				"url(#leagueShieldGradient_" +
																				championship.league.id +
																				")"
																			}
																			fillRule="nonzero"
																		/>
																	</g>
																</svg>
															</div>
															<div className="content">
																<div className="rankingLabel">
																	{_("championship.leagueRanking")}
																</div>
																<div className="position">
																	{championship.league.ranking}
																</div>
															</div>
														</div>
													)}
												</div>
											) : null}
											{championship.optedIn && !isMyProfile ? (
												<div className="itemFooter">
													<ChatLink
														ids={[playerProfile.userId]}
														label={_(
															"championship.challengeToWinPoints",
															championship.winPoints,
															{ points: championship.winPoints }
														)}
														className="button white fullWidth"
														type="challenge"
														params={challengeButtonParams}
													/>
												</div>
											) : null}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}
				{playerProfile.favorites && playerProfile.favorites.length > 0 ? (
					<>
						<h6>{_("profile.favoriteClubs.heading")}</h6>
						<ClubsList clubs={playerProfile.favorites || []} />
					</>
				) : null}
			</div>
			{contextParams.selectForReservation && (
				<div className="modalFooter">
					<button
						className="button primaryButton large rounded"
						onClick={e => {
							e.stopPropagation();
							contextParams.selectForReservation();
							closePlayerPopup();
						}}
					>
						{_("profile.selectPartner")}
					</button>
				</div>
			)}
		</div>
	);
};

const PlayerProfileRating: FC<Profile> = ({ userId, rating }: Profile) => {
	if (!rating?.ITN) {
		return null;
	}

	// We need to filter out ITN number because API puts an "ITN" label into it
	const ITN = rating.ITN.match(/[+-]?\d+(\.\d+)?/g).toString();

	if (rating.officialRating && rating.officialRating.code && rating.officialRating.name) {
		return (
			<OfficialProfileRating
				country={rating.country}
				ratingValue={rating.ratingValue}
				ITN={ITN}
				ITNName={rating.ITNName}
				officialRating={rating.officialRating}
				userId={userId}
			/>
		);
	} else {
		return (
			<DefaultProfileRating
				country={rating.country}
				ratingValue={rating.ratingValue}
				ITN={ITN}
				ITNName={rating.ITNName}
				officialRating={rating.officialRating}
				userId={userId}
			/>
		);
	}
};

const DefaultProfileRating: FC<RatingProps> = ({ country, ratingValue, ITN, ITNName }: RatingProps) => {
	return (
		<div className="skillWrapper">
			{country && ratingValue ? (
				<div className="skill">
					<div className="icon" data-country={country} />
					<div className="label">
						{_("player.skill.rating")}: {ratingValue} ({ITNName})
					</div>
				</div>
			) : (
				<div className="skill">
					<div className="label">
						{_("player.skill.itn")}: {ITN} ({ITNName})
					</div>
				</div>
			)}
		</div>
	);
};

const OfficialProfileRating: FC<RatingProps> = ({ userId, ratingValue, officialRating, ITN, ITNName }: RatingProps) => {
	return (
		<div className="skillWrapper verifiedRating">
			{officialRating && ratingValue ? (
				<div
					className="skill"
					onClick={() => {
						openOfficialRatingPopup(userId, officialRating);
						closePlayerPopup();
					}}
				>
					<div className="icon" data-federation={officialRating.code} />
					<div className="label">
						{_("player.skill.rating")}: {ratingValue} ({ITNName})
					</div>
					<div className="disclosureindicator" />
				</div>
			) : (
				<div className="skill">
					<div className="label">
						{_("player.skill.itn")}: {ITN} ({ITNName})
					</div>
				</div>
			)}
			<div className="verifiedRatingLabel">
				<div className="icon" />
				{_("player.skill.ratingVerifiedBy", null, { federationName: officialRating.name })}
			</div>
		</div>
	);
};

export default withRedux({ loadFriends, sendBuddyRequest, showPlayerChampionshipProfile }, () => {
	return {};
})(PlayerProfile);
